import React, {useState, useEffect, useCallback} from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import Map, {Marker} from 'react-map-gl';
import {MapPin} from 'lucide-react';

interface LocationPickerProps {
  initialLatitude: number;
  initialLongitude: number;
  onLocationChange: (lat: number, lng: number) => void;
  height?: string;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  initialLatitude,
  initialLongitude,
  onLocationChange,
  height = '400px'
}) => {
  const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

  const [viewState, setViewState] = useState({
    latitude: initialLatitude,
    longitude: initialLongitude,
    zoom: 12
  });

  const [selectedLocation, setSelectedLocation] = useState({
    lat: initialLatitude,
    lng: initialLongitude
  });

  // Update view state when initial coordinates change
  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      latitude: initialLatitude,
      longitude: initialLongitude
    }));
    setSelectedLocation({
      lat: initialLatitude,
      lng: initialLongitude
    });
  }, [initialLatitude, initialLongitude]);

  const handleMapClick = useCallback((event: any) => {
    const {lng, lat} = event.lngLat;
    setSelectedLocation({lat, lng});
    onLocationChange(lat, lng);
  }, [onLocationChange]);

  const handleViewStateChange = useCallback((event: any) => {
    setViewState(event.viewState);
  }, []);

  if (!MAPBOX_TOKEN) {
    return (
      <div
        className="border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center"
        style={{height}}
      >
        <div className="text-center text-gray-500">
          <MapPin className="h-12 w-12 mx-auto mb-2" />
          <p>Mapbox token غير متوفر</p>
          <p className="text-sm">يرجى إضافة VITE_MAPBOX_ACCESS_TOKEN إلى ملف البيئة</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <Map
        {...viewState}
        onMove={handleViewStateChange}
        onClick={handleMapClick}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{width: '100%', height}}
        cursor="crosshair"
      >
        {/* Selected Location Marker */}
        <Marker
          latitude={selectedLocation.lat}
          longitude={selectedLocation.lng}
          anchor="bottom"
        >
          <div className="relative">
            <MapPin className="h-8 w-8 text-red-500 drop-shadow-lg" />
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-white px-2 py-1 rounded text-xs shadow-md border">
              الموقع المحدد
            </div>
          </div>
        </Marker>
      </Map>

      {/* Coordinates Display */}
      <div className="p-3 bg-gray-50 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">خط العرض:</span>
          <span className="font-mono font-medium">{selectedLocation.lat.toFixed(6)}</span>
        </div>
        <div className="flex justify-between items-center text-sm mt-1">
          <span className="text-gray-600">خط الطول:</span>
          <span className="font-mono font-medium">{selectedLocation.lng.toFixed(6)}</span>
        </div>
      </div>
    </div>
  );
};
